import { Component, For, Show, createSignal } from 'solid-js';
import type { AgentConfig, PanelConfig, AgentDiscipline, KnowledgeItem } from '../types/agents';
import { fetchUrlText } from '../services/knowledge';

interface AgentsPanelProps {
  panel: PanelConfig;
  onPanelChange: (panel: PanelConfig) => void;
}

const DISCIPLINES: AgentDiscipline[] = ['Generalist','Researcher','Architect','Coder','Reviewer','Product','Analyst','Writer'];

const PRESETS: Array<{ label: string; apply: (a: AgentConfig) => Partial<AgentConfig> }> = [
  { label: '— Preset —', apply: () => ({}) },
  { label: 'Critic', apply: () => ({ discipline: 'Reviewer', temperature: 0.4, systemPrompt: 'You are a rigorous critic. Identify flaws, risks, and missing edge cases. Be concise but specific.' }) },
  { label: 'Planner', apply: () => ({ discipline: 'Architect', temperature: 0.5, systemPrompt: 'You are a project planner. Produce step-by-step plans, milestones, and risks with mitigations.' }) },
  { label: 'Coder Reviewer', apply: () => ({ discipline: 'Reviewer', temperature: 0.3, systemPrompt: 'You are a strict code reviewer. Focus on correctness, readability, performance, and security.' }) },
  { label: 'Researcher', apply: () => ({ discipline: 'Researcher', temperature: 0.4, systemPrompt: 'You are a meticulous research assistant. Gather facts, compare viewpoints, and cite sources succinctly.' }) },
  { label: 'Writer', apply: () => ({ discipline: 'Writer', temperature: 0.7, systemPrompt: 'You are a clear, concise writer. Synthesize inputs into crisp, actionable output tailored to the audience.' }) },
];

export const AgentsPanel: Component<AgentsPanelProps> = (props) => {
  const [newName, setNewName] = createSignal('');
  const [addingUrl, setAddingUrl] = createSignal('');
  const [addingText, setAddingText] = createSignal('');
  const [busy, setBusy] = createSignal<string | null>(null);

  const defaultPanel = (): PanelConfig => ({
    roundRobin: true,
    maxRounds: 2,
    shareKnowledgeAcrossAgents: true,
    summarizerEnabled: true,
    summarizerPrompt: 'Be concise, list key decisions, unresolved questions, and next actions.',
    dynamicOrchestration: true,
    agents: [
      { id: 'agent-orchestrator', name: 'Orchestrator', discipline: 'Architect', systemPrompt: 'You are an expert orchestrator. Your job is to analyze the user\'s request and propose the smallest, most effective panel of experts needed to solve it. Return a structured plan with agent roles and brief instructions for each. Consider if web search or external knowledge is needed.', temperature: 0.4, knowledge: [], enabled: true, origin: 'manual', provider: 'webllm' }
    ]
  });

  const updateAgent = (id: string, patch: Partial<AgentConfig>) => {
    const next = { ...props.panel, agents: props.panel.agents.map(a => a.id === id ? { ...a, ...patch } : a) };
    props.onPanelChange(next);
  };
  const addAgent = () => {
    const id = `agent-${Date.now()}`;
    const name = newName().trim() || `Agent ${props.panel.agents.length + 1}`;
    const agent: AgentConfig = { id, name, discipline: 'Generalist', systemPrompt: '', temperature: 0.7, knowledge: [], enabled: true, provider: 'webllm' };
    props.onPanelChange({ ...props.panel, agents: [...props.panel.agents, agent] });
    setNewName('');
  };
  const removeAgent = (id: string) => {
    props.onPanelChange({ ...props.panel, agents: props.panel.agents.filter(a => a.id !== id) });
  };
  const move = (id: string, delta: number) => {
    const list = [...props.panel.agents];
    const i = list.findIndex(a => a.id === id);
    if (i < 0) return;
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    const [item] = list.splice(i,1);
    list.splice(j,0,item);
    props.onPanelChange({ ...props.panel, agents: list });
  };
  const addKnowledgeTo = async (id: string, type: 'text'|'url') => {
    const agent = props.panel.agents.find(a => a.id === id);
    if (!agent) return;
    setBusy(id);
    try {
      if (type === 'url') {
        const url = addingUrl().trim();
        if (!url) return;
        const text = await fetchUrlText(url);
        const urlItem: KnowledgeItem = { id: `url-${Date.now()}`, type: 'url', value: url, addedAt: Date.now() };
        const textItem: KnowledgeItem = { id: `text-${Date.now()}`, type: 'text', value: text, addedAt: Date.now() };
        agent.knowledge = [urlItem, textItem, ...agent.knowledge];
        setAddingUrl('');
      } else {
        const txt = addingText().trim();
        if (!txt) return;
        const textItem: KnowledgeItem = { id: `text-${Date.now()}`, type: 'text', value: txt, addedAt: Date.now() };
        agent.knowledge = [textItem, ...agent.knowledge];
        setAddingText('');
      }
      updateAgent(id, { knowledge: agent.knowledge });
    } finally {
      setBusy(null);
    }
  };
  const addKnowledgeAll = async (type: 'text'|'url') => {
    setBusy('all');
    try {
      if (type === 'url') {
        const url = addingUrl().trim();
        if (!url) return;
        const text = await fetchUrlText(url);
        const patch = (a: AgentConfig) => {
          const urlItem: KnowledgeItem = { id: `url-${Date.now()}`, type: 'url', value: url, addedAt: Date.now() };
          const textItem: KnowledgeItem = { id: `text-${Date.now()}`, type: 'text', value: text, addedAt: Date.now() };
          return { ...a, knowledge: [urlItem, textItem, ...a.knowledge] };
        };
        props.onPanelChange({ ...props.panel, agents: props.panel.agents.map(patch) });
        setAddingUrl('');
      } else {
        const txt = addingText().trim();
        if (!txt) return;
        const patch = (a: AgentConfig) => {
          const textItem: KnowledgeItem = { id: `text-${Date.now()}`, type: 'text', value: txt, addedAt: Date.now() };
          return { ...a, knowledge: [textItem, ...a.knowledge] };
        };
        props.onPanelChange({ ...props.panel, agents: props.panel.agents.map(patch) });
        setAddingText('');
      }
    } finally {
      setBusy(null);
    }
  };

  const enabledCount = () => props.panel.agents.filter(a=>a.enabled).length;
  const totalCount = () => props.panel.agents.length;
  const summaryLine = () => `Agents: ${enabledCount()}/${totalCount()} • Rounds: ${props.panel.maxRounds} • ${props.panel.roundRobin?'Round-robin':'Single pass'} • Share: ${props.panel.shareKnowledgeAcrossAgents?'On':'Off'} • Summary: ${props.panel.summarizerEnabled?'On':'Off'} • Dynamic: ${props.panel.dynamicOrchestration?'On':'Off'}`;

  return (
    <section class="agents-panel" role="region" aria-label="Agents configuration">
      <details class="agents-details">
        <summary class="agents-summary">
          <h3>Agent panel</h3>
          <span class="agents-meta">{summaryLine()}</span>
        </summary>
        <header class="agents-header">
        <div class="agents-global">
          <label>
            <input type="checkbox" checked={!!props.panel.dynamicOrchestration} onChange={(e) => props.onPanelChange({ ...props.panel, dynamicOrchestration: e.currentTarget.checked })} />
            Dynamic orchestration (single orchestrator chooses experts per prompt)
          </label>
          <label>
            <input type="checkbox" checked={props.panel.roundRobin} onChange={(e) => props.onPanelChange({ ...props.panel, roundRobin: e.currentTarget.checked })} />
            Round-robin turns
          </label>
          <label>
            Max rounds
            <input type="number" min="1" max="10" value={props.panel.maxRounds} onInput={(e) => props.onPanelChange({ ...props.panel, maxRounds: Number(e.currentTarget.value) })} />
          </label>
          <label>
            <input type="checkbox" checked={props.panel.shareKnowledgeAcrossAgents} onChange={(e) => props.onPanelChange({ ...props.panel, shareKnowledgeAcrossAgents: e.currentTarget.checked })} />
            Share knowledge to all
          </label>
          <label>
            <input type="checkbox" checked={!!props.panel.summarizerEnabled} onChange={(e) => props.onPanelChange({ ...props.panel, summarizerEnabled: e.currentTarget.checked })} />
            Summary at end of round
          </label>
        </div>
        <div class="agents-global">
          <label class="agents-summary-prompt">
            Summary prompt
            <input type="text" placeholder="Optional summary style/instructions" value={props.panel.summarizerPrompt || ''} onInput={(e)=>props.onPanelChange({ ...props.panel, summarizerPrompt: e.currentTarget.value })} />
          </label>
          <button type="button" onClick={() => {
            const enable = !props.panel.agents.some(a => a.enabled);
            props.onPanelChange({ ...props.panel, agents: props.panel.agents.map(a => ({ ...a, enabled: enable })) });
          }}>
            {props.panel.agents.some(a => a.enabled) ? 'Disable all' : 'Enable all'}
          </button>
          <button type="button" onClick={() => {
            if (confirm('Reset agents to the default Orchestrator? This will overwrite current agent settings.')) {
              props.onPanelChange(defaultPanel());
            }
          }}>
            Reset to defaults
          </button>
        </div>
        <div class="agents-add">
          <input type="text" placeholder="New agent name" value={newName()} onInput={(e)=>setNewName(e.currentTarget.value)} />
          <button type="button" onClick={addAgent}>Add agent</button>
        </div>
        <div class="agents-knowledge">
          <input type="url" placeholder="Paste URL" value={addingUrl()} onInput={(e)=>setAddingUrl(e.currentTarget.value)} />
          <button type="button" disabled={busy()!==null} onClick={()=>addKnowledgeAll('url')}>{busy()==='all'?'Adding…':'Add URL to all'}</button>
          <textarea rows={2} placeholder="Paste text" value={addingText()} onInput={(e)=>setAddingText(e.currentTarget.value)} />
          <button type="button" disabled={busy()!==null} onClick={()=>addKnowledgeAll('text')}>{busy()==='all'?'Adding…':'Add text to all'}</button>
        </div>
        </header>

        <div class="agents-list">
        <For each={props.panel.agents}>
          {(a) => (
            <details class="agent-card">
              <summary class="agent-card-header">
                <div class="agent-title">
                  <input class="agent-name" value={a.name} onInput={(e)=>updateAgent(a.id, { name: e.currentTarget.value })} aria-label="Agent name" />
                  <select value={a.discipline} onChange={(e)=>updateAgent(a.id, { discipline: e.currentTarget.value as any })} aria-label="Discipline">
                    <For each={DISCIPLINES}>{(d)=>(<option value={d}>{d}</option>)}</For>
                  </select>
                </div>
                <div class="agent-controls">
                  <button type="button" class="icon-btn" onClick={()=>move(a.id,-1)} title="Move left" aria-label="Move left">◀</button>
                  <button type="button" class="icon-btn" onClick={()=>move(a.id,1)} title="Move right" aria-label="Move right">▶</button>
                  <label>
                    <input type="checkbox" checked={a.enabled} onChange={(e)=>updateAgent(a.id, { enabled: e.currentTarget.checked })} /> Enabled
                  </label>
                  <button type="button" class="icon-btn" onClick={()=>removeAgent(a.id)} title="Remove" aria-label="Remove">🗑️</button>
                </div>
              </summary>
              <div class="agent-body">
                <label>System prompt
                  <textarea rows={3} value={a.systemPrompt} onInput={(e)=>updateAgent(a.id, { systemPrompt: e.currentTarget.value })} />
                </label>
                <div class="agent-preset">
                  <label>Preset
                    <select onChange={(e) => {
                      const preset = PRESETS.find(p => p.label === e.currentTarget.value);
                      if (!preset) return;
                      const patch = preset.apply(a);
                      updateAgent(a.id, patch);
                    }}>
                      {PRESETS.map(p => (<option value={p.label}>{p.label}</option>))}
                    </select>
                  </label>
                </div>
                <div class="agent-sampling">
                  <label>Temp <input type="range" min="0" max="1" step="0.01" value={a.temperature} onInput={(e)=>updateAgent(a.id, { temperature: Number(e.currentTarget.value) })} /></label>
                  <label>Top P <input type="number" inputMode="decimal" step="0.01" value={a.topP ?? '' as any} onInput={(e)=>updateAgent(a.id, { topP: e.currentTarget.value? Number(e.currentTarget.value): undefined })} /></label>
                  <label>Max tokens <input type="number" inputMode="numeric" value={a.maxTokens ?? '' as any} onInput={(e)=>updateAgent(a.id, { maxTokens: e.currentTarget.value? Number(e.currentTarget.value): undefined })} /></label>
                </div>
                <div class="agent-provider">
                  <fieldset>
                    <legend>Provider</legend>
                    <label>
                      <select value={a.provider || 'webllm'} onChange={(e)=>updateAgent(a.id, { provider: e.currentTarget.value as any })}>
                        <option value="webllm">WebLLM (local)</option>
                        <option value="openai">OpenAI-compatible API</option>
                      </select>
                    </label>
                    <Show when={(a.provider || 'webllm') === 'webllm'}>
                      <label>WebLLM model
                        <input type="text" placeholder="Leave blank to use global model" value={a.modelId || ''}
                          onInput={(e)=>updateAgent(a.id, { modelId: e.currentTarget.value || undefined })} />
                      </label>
                    </Show>
                    <Show when={(a.provider || 'webllm') === 'openai'}>
                      <div class="endpoint-grid">
                        <label>Base URL
                          <input type="url" placeholder="https://api.openai.com" value={a.endpoint?.baseUrl || ''}
                            onInput={(e)=>updateAgent(a.id, { endpoint: { ...(a.endpoint||{ baseUrl: '' }), baseUrl: e.currentTarget.value } })} />
                        </label>
                        <label>Path
                          <input type="text" placeholder="/v1/chat/completions" value={a.endpoint?.path || ''}
                            onInput={(e)=>updateAgent(a.id, { endpoint: { ...(a.endpoint||{ baseUrl: '' }), path: e.currentTarget.value || undefined } })} />
                        </label>
                        <label>API Key
                          <input type="password" placeholder="sk-..." value={a.endpoint?.apiKey || ''}
                            onInput={(e)=>updateAgent(a.id, { endpoint: { ...(a.endpoint||{ baseUrl: '' }), apiKey: e.currentTarget.value || undefined } })} />
                        </label>
                        <label>Model
                          <input type="text" placeholder="gpt-4o-mini" value={a.endpoint?.model || ''}
                            onInput={(e)=>updateAgent(a.id, { endpoint: { ...(a.endpoint||{ baseUrl: '' }), model: e.currentTarget.value || undefined } })} />
                        </label>
                      </div>
                    </Show>
                  </fieldset>
                </div>
                <div class="agent-knowledge">
                  <div class="agent-knowledge-add">
                    <input type="url" placeholder="URL" value={addingUrl()} onInput={(e)=>setAddingUrl(e.currentTarget.value)} />
                    <button type="button" disabled={busy()===a.id} onClick={()=>addKnowledgeTo(a.id,'url')}>{busy()===a.id?'Adding…':'Add URL'}</button>
                    <textarea rows={2} placeholder="Text" value={addingText()} onInput={(e)=>setAddingText(e.currentTarget.value)} />
                    <button type="button" disabled={busy()===a.id} onClick={()=>addKnowledgeTo(a.id,'text')}>{busy()===a.id?'Adding…':'Add text'}</button>
                  </div>
                  <Show when={a.knowledge.length>0}>
                    <ul class="knowledge-list">
                      <For each={a.knowledge}>
                        {(k) => (
                          <li>
                            <span class="k-type">{k.type}</span>
                            <span class="k-value" title={k.value}>{k.type==='url'? k.value : `${k.value.slice(0,80)}${k.value.length>80?'…':''}`}</span>
                            <time class="k-time" datetime={new Date(k.addedAt).toISOString()}>{new Date(k.addedAt).toLocaleString()}</time>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </div>
              </div>
            </details>
          )}
        </For>
        </div>
      </details>
    </section>
  );
};
